import { Translatable } from './annotation';
import { TakesReferenceKind } from './field-schema';
import { ReferenceKind } from './result';

// The catalog itself comes from the service (FieldCatalogService); nothing here may list fields.

/** Where a field lives: a header value, a column of every position, or a document total. */
export type FieldScope = 'header' | 'item' | 'total';

export type FieldVisibility = 'primary' | 'secondary';

export interface FieldDefinition {
  /**
   * `header`: path into the result; `item`/`total`: path inside an `Item`. A `[]` marks a
   * list — the value is one of its entries (`metadata.emails[]`, `discounts[].value`).
   */
  path: string;
  scope: FieldScope;
  group: string;
  label: Translatable;
  required: boolean;
  /** At least one field of this group must be filled (`item_identifier`: article no. or EAN). */
  requiredOneOf: string | null;
  multiline: boolean;
  /** `secondary` fields sit behind "Weitere Felder". */
  visibility: FieldVisibility;
  repeated: boolean;
  /** Key in `field_bboxes` when it differs from `path`. */
  bboxKey: string | null;
  /** The list entry carries the box (`page`, `bbox`), not `field_bboxes`. */
  bboxInEntry: boolean;
  /**
   * DraftBox category when the value goes into a template; null for a field that is only
   * shown — it comes from a reference (`derivedFrom`) and is corrected there.
   */
  templateCategory: string | null;
  takesReferenceKind: TakesReferenceKind | null;
  /** Filled from the first reference of this kind when it was not read itself. */
  derivedFrom: ReferenceKind | null;
  /** Hint for the user who marks the field. */
  hint: string | null;
}

export interface FieldGroupDefinition {
  key: string;
  label: Translatable;
}

export interface AreaDefinition {
  category: string;
  label: Translatable;
  takesReferenceKind?: TakesReferenceKind | null;
}

export interface ReferenceKindDefinition {
  kind: ReferenceKind;
  label: Translatable;
}

/** Which fields a document type has, how they show and how they go into a template. */
export interface FieldCatalog {
  fields: readonly FieldDefinition[];
  groups: readonly FieldGroupDefinition[];
  areas: readonly AreaDefinition[];
  referenceKinds: readonly ReferenceKindDefinition[];
}

export const EMPTY_FIELD_CATALOG: FieldCatalog = {
  fields: [],
  groups: [],
  areas: [],
  referenceKinds: [],
};

/**
 * Result path of a field. Header values and totals carry their full path (`items[0].net`);
 * item fields are relative to the given position. Lists keep `[]`.
 */
export function pathOf(field: FieldDefinition, itemPath?: string): string {
  return field.scope === 'item' ? `${itemPath}.${field.path}` : field.path;
}

/** `a.b[].c` → `{ list: 'a.b', rest: '.c' }`; null for a field that is no list entry. */
export function splitList(path: string): { list: string; rest: string } | null {
  const at = path.indexOf('[]');
  return at < 0 ? null : { list: path.slice(0, at), rest: path.slice(at + 2) };
}

/** The concrete path of one list entry: `a.b[].c`, 2 → `a.b[2].c`. */
export function entryPath(path: string, index: number): string {
  return path.replace('[]', `[${index}]`);
}

/** List indexes inside a path become `[]` again; position indexes (`items[n]`) stay. */
export function templateOfPath(path: string): string {
  return path.replace(/(?<!items)\[\d+\]/g, '[]');
}

export function fieldsOf(catalog: FieldCatalog, scope: FieldScope): FieldDefinition[] {
  return catalog.fields.filter((field) => field.scope === scope);
}

/** The catalog field a concrete result path points at, if any. */
export function fieldForPath(
  catalog: FieldCatalog,
  path: string
): FieldDefinition | undefined {
  const template = templateOfPath(path);
  const absolute = catalog.fields.find((f) => f.scope !== 'item' && f.path === template);
  if (absolute) return absolute;
  const match = /^items\[0\](?:\.items\[\d+\])+\.(.+)$/.exec(template);
  return match
    ? catalog.fields.find((f) => f.scope === 'item' && f.path === match[1])
    : undefined;
}

/**
 * The field that decides which entries of a shared list belong to a group.
 * The service: "Skonto-Einträge erkennt man an `days`" — `items[0].discounts` also holds
 * other document discounts.
 * FIXME: derived from the catalog shape (a `[].days` field next to the others in its group);
 * ask the service for an explicit entry filter in the schema.
 */
export function entryDiscriminator(
  catalog: FieldCatalog,
  field: FieldDefinition
): string | null {
  const own = splitList(field.path);
  if (!own) return null;
  const marker = catalog.fields.find((other) => {
    const list = splitList(other.path);
    return (
      other.group === field.group &&
      other.scope === field.scope &&
      list?.list === own.list &&
      list.rest === '.days'
    );
  });
  return marker ? 'days' : null;
}

/** Whether a box of the category carries a reference kind (fields and area tags alike). */
export function takesReferenceKind(
  catalog: FieldCatalog,
  category: string
): TakesReferenceKind | null {
  return (
    catalog.areas.find((area) => area.category === category)?.takesReferenceKind ??
    catalog.fields.find((field) => field.templateCategory === category)
      ?.takesReferenceKind ??
    null
  );
}

/** Label of a draft category: an area tag or a catalog field; the category itself else. */
export function categoryLabel(catalog: FieldCatalog, category: string): Translatable {
  return (
    catalog.areas.find((area) => area.category === category)?.label ??
    catalog.fields.find((field) => field.templateCategory === category)?.label ??
    category
  );
}
